// What a pre-lead row shows: the colour of its status tag, the tag's words and the next step.
import { POSITIVE_INTENTS, type PreLead } from "./types";

type Translate = (key: string, args?: Record<string, unknown>) => string;
export type Tone = "blue" | "muted" | "green" | "red" | "grey";

export const TONE_CLASSES: Record<Tone, string> = {
  blue: "bg-secondary text-secondary-foreground",
  muted: "bg-muted text-muted-foreground",
  green: "bg-green-100 text-green-900 dark:bg-green-900/40 dark:text-green-100",
  red: "bg-red-100 text-red-900 dark:bg-red-900/40 dark:text-red-100",
  grey: "bg-zinc-200 text-zinc-700 dark:bg-zinc-700 dark:text-zinc-200",
};

const positive = (p: PreLead) =>
  (POSITIVE_INTENTS as readonly string[]).includes(p.reply_intent ?? "");

/** Sender and recipient can change only until the first email is approved */
export const beforeApproval = (p: PreLead) =>
  ["found", "kept", "ready"].includes(p.status);

/** The tick stopped the follow-ups (three failures, or a stuck draft): Resume puts them back on the due list */
export const followupsStopped = (p: PreLead) =>
  p.status === "sent" && p.needs_reply && p.next_touch_at == null;

/** A positive reply that is waiting: Convert to lead makes it a deal */
export const canConvert = (p: PreLead) => p.status === "replied" && positive(p);

export const statusTone = (p: PreLead): Tone => {
  switch (p.status) {
    case "approved":
    case "sent":
      return "muted";
    case "replied":
      if (!p.reply_intent) return "blue";
      return positive(p) ? "green" : "red";
    case "converted":
      return "green";
    case "closed":
      return "grey";
    default:
      return "blue";
  }
};

export const statusLabel = (p: PreLead, translate: Translate) => {
  if (p.status === "sent" && p.touch_count)
    return translate("resources.pre_leads.tag.sent", {
      touch: p.touch_count,
      max: p.max_touches,
    });
  if (p.status === "replied" && p.reply_intent)
    return translate("resources.pre_leads.tag.replied", {
      intent: translate(`resources.notes.intent.values.${p.reply_intent}`),
    });
  return translate(`resources.pre_leads.statuses.${p.status}`);
};

/** "Thu 30 Oct" */
const day = (iso: string) =>
  new Date(iso).toLocaleDateString(undefined, {
    weekday: "short",
    day: "numeric",
    month: "short",
  });

export const nextTextFor = (p: PreLead, translate: Translate): string => {
  const key = (k: string) => `resources.pre_leads.next.${k}`;
  switch (p.status) {
    case "kept": {
      const log = Array.isArray(p.evidence?.log)
        ? (p.evidence.log as string[])
        : [];
      return log.at(-1) ?? translate(key("kept"));
    }
    case "approved": {
      const at = p.next_touch_at ?? p.approved_at;
      return at
        ? translate(key("approved"), { date: day(at) })
        : translate(key("approved_soon"));
    }
    case "sent":
      return p.next_touch_at
        ? translate(key("sent"), { date: day(p.next_touch_at) })
        : translate(key("waiting"));
    case "closed":
      return translate(
        p.closed_reason
          ? `resources.pre_leads.closed_reasons.${p.closed_reason}`
          : "resources.pre_leads.statuses.closed",
      );
    default:
      return translate(key(p.status));
  }
};
